/**
 * Client Timeline Tab
 * 
 * Visual chronological log of all major events in the client's history.
 */
import React, { useState, useEffect } from "react";
import { 
  Clock, Filter, ChevronDown, ChevronUp, User, Calendar,
  FileText, Heart, AlertTriangle, CheckCircle, XCircle,
  UserPlus, UserMinus, Home, Building, MessageSquare, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for event types
const EVENT_ICONS = {
  referral_received: FileText,
  assessment_started: FileText,
  assessment_completed: CheckCircle,
  care_plan_generated: Heart,
  care_plan_approved: CheckCircle,
  care_plan_updated: RefreshCw,
  client_activated: CheckCircle,
  client_on_hold: AlertTriangle,
  client_resumed: CheckCircle,
  client_discharged: XCircle,
  caregiver_assigned: UserPlus,
  caregiver_unassigned: UserMinus,
  assignment_accepted: CheckCircle,
  assignment_declined: XCircle,
  visit_scheduled: Calendar,
  visit_completed: CheckCircle,
  visit_missed: AlertTriangle,
  visit_cancelled: XCircle,
  incident_reported: AlertTriangle,
  hospitalization: Building,
  fall_reported: AlertTriangle,
  document_uploaded: FileText,
  note_added: MessageSquare,
  status_changed: RefreshCw,
};

// Color mapping for event categories
const CATEGORY_COLORS = {
  intake: "border-l-blue-500",
  care_plan: "border-l-purple-500",
  status: "border-l-green-500",
  assignment: "border-l-cyan-500",
  visit: "border-l-amber-500",
  incident: "border-l-red-500",
  document: "border-l-slate-500",
  communication: "border-l-indigo-500",
  system: "border-l-gray-400",
  custom: "border-l-gray-400",
};

export default function ClientTimelineTab({ clientId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategory] = useState("all");
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 30;

  useEffect(() => {
    fetchEvents(true);
  }, [clientId, categoryFilter]);

  const fetchEvents = async (reset = false) => {
    if (reset) {
      setOffset(0);
      setLoading(true);
    }
    
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: reset ? "0" : offset.toString(),
      });
      
      if (categoryFilter !== "all") {
        params.append("category", categoryFilter);
      }
      
      const res = await fetch(
        `${API}/api/timeline/client/${clientId}?${params.toString()}`,
        { credentials: "include" }
      );
      
      if (res.ok) {
        const data = await res.json();
        if (reset) {
          setEvents(data.events || []);
        } else {
          setEvents(prev => [...prev, ...(data.events || [])]);
        }
        setHasMore(data.total > (reset ? limit : offset + limit));
        if (!reset) {
          setOffset(prev => prev + limit);
        }
      }
    } catch (err) {
      console.error("Failed to fetch timeline:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (eventId) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  // Group events by date
  const eventsByDate = events.reduce((acc, event) => {
    const date = event.event_at?.split("T")[0] || event.created_at?.split("T")[0];
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (dateStr === today.toISOString().split("T")[0]) {
      return "Today";
    } else if (dateStr === yesterday.toISOString().split("T")[0]) {
      return "Yesterday";
    }
    
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-4" data-testid="timeline-tab">
      {/* Filter */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Activity Timeline
        </h3>
        <Select value={categoryFilter} onValueChange={setCategory}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="intake">Intake</SelectItem>
            <SelectItem value="care_plan">Care Plan</SelectItem>
            <SelectItem value="status">Status Changes</SelectItem>
            <SelectItem value="assignment">Assignments</SelectItem>
            <SelectItem value="visit">Visits</SelectItem>
            <SelectItem value="incident">Incidents</SelectItem>
            <SelectItem value="document">Documents</SelectItem>
            <SelectItem value="communication">Communication</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">
          Loading timeline...
        </div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              {categoryFilter !== "all" 
                ? "No events in this category"
                : "No events recorded yet"
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(eventsByDate).map(([date, dateEvents]) => (
            <div key={date}>
              {/* Date Header */}
              <div className="sticky top-0 bg-background/95 backdrop-blur z-10 py-2 mb-3">
                <span className="text-sm font-medium text-muted-foreground">
                  {formatDate(date)}
                </span>
              </div>
              
              {/* Events for this date */}
              <div className="space-y-3 relative before:absolute before:left-[17px] before:top-0 before:bottom-0 before:w-px before:bg-border">
                {dateEvents.map((event) => {
                  const Icon = EVENT_ICONS[event.event_type] || Clock;
                  const isExpanded = expandedEvents.has(event.id);
                  const categoryColor = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.custom;
                  const displayConfig = event.display || {};
                  
                  return (
                    <div 
                      key={event.id}
                      className={`relative pl-10 border-l-4 ml-[15px] ${categoryColor}`}
                    >
                      {/* Timeline dot */}
                      <div 
                        className="absolute left-[-24px] top-1 w-6 h-6 rounded-full bg-background border-2 border-border flex items-center justify-center"
                      >
                        <Icon className={`w-3 h-3 ${
                          displayConfig.color === "red" ? "text-red-500" :
                          displayConfig.color === "green" ? "text-green-500" :
                          displayConfig.color === "amber" || displayConfig.color === "orange" ? "text-amber-500" :
                          displayConfig.color === "blue" ? "text-blue-500" :
                          displayConfig.color === "purple" ? "text-purple-500" :
                          "text-muted-foreground"
                        }`} />
                      </div>
                      
                      <Card 
                        className="cursor-pointer hover:shadow-sm transition-shadow"
                        onClick={() => toggleExpand(event.id)}
                      >
                        <CardContent className="py-3 px-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm">
                                  {event.title}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatTime(event.event_at || event.created_at)}
                                </span>
                              </div>
                              
                              {event.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                  {event.description}
                                </p>
                              )}
                              
                              {/* Expanded content */}
                              {isExpanded && (
                                <div className="mt-3 pt-3 border-t text-sm space-y-2">
                                  {event.actor_name && (
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                      <User className="w-3 h-3" />
                                      <span>By {event.actor_name}</span>
                                      {event.actor_role && (
                                        <Badge variant="outline" className="text-xs">
                                          {event.actor_role.replace(/_/g, " ")}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                                  
                                  {event.metadata && Object.keys(event.metadata).length > 0 && (
                                    <div className="bg-slate-50 rounded p-2 text-xs">
                                      <pre className="whitespace-pre-wrap">
                                        {JSON.stringify(event.metadata, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <Button variant="ghost" size="sm" className="shrink-0 h-6 w-6 p-0">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          
          {/* Load More */}
          {hasMore && (
            <div className="text-center pt-4">
              <Button 
                variant="outline" 
                onClick={() => fetchEvents(false)}
                disabled={loading}
              >
                Load More Events
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
